import * as React from "react"
import { cn } from "../../lib/utils"

const Table = React.forwardRef(({ className, ...props }, ref) => (
  <div className="relative w-full overflow-auto">
    <table
      ref={ref}
      className={cn("w-full caption-bottom text-sm", className)}
      {...props}
    />
  </div>
))
Table.displayName = "Table"

/*
 * A header row is a label strip, not a first row of data.
 *
 * Stock shadcn gives it the same background and near-enough the same type as
 * the body, so a table reads as an undifferentiated grid. Recessing it onto
 * the muted surface separates the two jobs — that is why a well-made data
 * table looks organised before you have read a word of it.
 *
 * The labels are 13px sentence case in the muted grey. They were 11px,
 * uppercase and letter-spaced, which read as texture rather than words on a
 * black ground; the recessed strip does the separating, so the type does not
 * have to shrink to do it too.
 */
const TableHeader = React.forwardRef(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn("border-b border-border bg-muted/60 [&_tr]:border-0", className)}
    {...props}
  />
))
TableHeader.displayName = "TableHeader"

const TableBody = React.forwardRef(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn("[&_tr:last-child]:border-0", className)}
    {...props}
  />
))
TableBody.displayName = "TableBody"

const TableFooter = React.forwardRef(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      "border-t bg-muted/50 font-medium [&>tr]:last:border-b-0",
      className
    )}
    {...props}
  />
))
TableFooter.displayName = "TableFooter"

const TableRow = React.forwardRef(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      // Dividers barely there: on a near-black ground a full-strength rule
      // between every row draws harder than the values it separates, and the
      // eye ends up reading the grid instead of the data.
      "border-b border-border/45 transition-colors duration-[130ms] last:border-0 hover:bg-accent/40",
      className
    )}
    {...props}
  />
))
TableRow.displayName = "TableRow"

const TableHead = React.forwardRef(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "h-10 px-4 text-left align-middle text-[13px] font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0",
      className
    )}
    {...props}
  />
))
TableHead.displayName = "TableHead"

const TableCell = React.forwardRef(({ className, ...props }, ref) => (
  <td
    ref={ref}
    // Taller rows than the stock `p-4`. Density is about how much you can
    // *read*, not how much you can fit: a 56px row with air around the values
    // scans faster than a 44px one packed to its edges.
    className={cn("px-4 py-3.5 align-middle [&:has([role=checkbox])]:pr-0", className)}
    {...props}
  />
))
TableCell.displayName = "TableCell"

const TableCaption = React.forwardRef(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-sm text-muted-foreground", className)}
    {...props}
  />
))
TableCaption.displayName = "TableCaption"

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}